import { formatChannelName } from '@seaber/common/core/channels';
import type { ScenarioTimeline_ShipFragment } from '../../../graphql';
import { isDefined } from '../../../lib/util';
import type { PortCallData } from '../../core/Core';
import { isPortCallEventTimeData } from '../../core/TimeData';
import type {
  ChannelCostEstimateItem,
  PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';
import type { Cost, CostsByPortCall } from './costs';
import { useCallback } from 'react';
import type { EventTimeline } from '../../core/Timelines';
import { getTimelinePortCalls } from '../shipment';

export type ChannelCostByChanel = {
  byVessel?: ChannelCostEstimateItem;
  byTypes?: (ChannelCostEstimateItem & { typeName: string })[];
  byName?: ChannelCostEstimateItem;
};

export const useChannelCosts = (portCostEstimateMaps: PortCostEstimateMaps) => {
  const getTimelineChannelCosts = useCallback(
    async (
      timeline: EventTimeline,
      channels: string[],
    ): Promise<{ channelCosts: Map<PortCallData['id'], Cost[]> } | null> => {
      if (timeline.type !== 'tc' || !timeline.vessel) {
        return null;
      }

      const { portCalls } = getTimelinePortCalls(timeline);

      const channelCosts = getChannelCostEstimates({
        channels,
        portCalls,
        portCostEstimateMaps,
        vessel: timeline.vessel,
      });

      return {
        channelCosts,
      };
    },
    [portCostEstimateMaps],
  );

  return {
    getTimelineChannelCosts,
  };
};

export function getChannelCostEstimates({
  portCostEstimateMaps,
  vessel,
  channels,
  portCalls,
}: {
  portCostEstimateMaps: PortCostEstimateMaps;
  vessel: ScenarioTimeline_ShipFragment;
  channels: string[];
  portCalls: PortCallData[];
}) {
  const costs: CostsByPortCall = new Map();

  const vesselId = vessel.id;
  const vesselTypes = vessel.vesselAttributesAssociationsByShipId.nodes
    .map((n) => {
      const name = n?.vesselAttributeByVesselAttributeId?.name;
      const id = n?.vesselAttributeByVesselAttributeId?.id;
      if (!id || !name) return;
      return { id, name };
    })
    .filter(isDefined);
  const vesselClass = vessel?.vesselAttributeByPrimaryAttributeId;

  const {
    channelByChannelName,
    channelByVesselId,
    channelByType,
    channelById,
  } = portCostEstimateMaps;

  const channelCostByChannels: Map<string, ChannelCostByChanel> = new Map();

  for (const channel of channels) {
    const byVessel = channelByVesselId.get(channel, vesselId);
    if (byVessel) {
      const existingCost = channelCostByChannels.get(channel);
      channelCostByChannels.set(channel, {
        ...existingCost,
        byVessel: {
          id: byVessel.id,
          cost: byVessel.cost,
          currency: byVessel.currency,
          durationOverhead: byVessel.durationOverhead,
        },
      });
    }
    for (const type of vesselTypes) {
      const byType = channelByType.get(channel, type.name);
      if (!byType) continue;
      const existingCost = channelCostByChannels.get(channel);
      const existingTypes = existingCost?.byTypes ?? [];
      channelCostByChannels.set(channel, {
        ...existingCost,
        byTypes: [
          ...existingTypes,
          {
            id: byType.id,
            cost: byType.cost,
            currency: byType.currency,
            typeName: byType.typeName,
            durationOverhead: byType.durationOverhead,
          },
        ],
      });
    }
    const byName = channelByChannelName.get(channel);
    if (byName) {
      const existingCost = channelCostByChannels.get(channel);
      channelCostByChannels.set(channel, {
        ...existingCost,
        byName: {
          id: byName.id,
          cost: byName.cost,
          currency: byName.currency,
          durationOverhead: byName.durationOverhead,
        },
      });
    }
  }

  for (const [index, pc] of portCalls.entries()) {
    const usedChannels = pc.tds
      .filter((td) => index !== 0 || !td.laden) // Filter out initial laden leg
      .flatMap((i) => i.usedChannels);
    if (!usedChannels.length) continue;

    const portCallEntry = pc?.tds.find(isPortCallEventTimeData)?.event.entry;
    if (!portCallEntry) continue;

    const channelCostDefaultId = portCallEntry?.channelCostDefaultId;
    const oneTimeOverrideChannelCost =
      portCallEntry?.oneTimeOverrideChannelCost;

    let cost = 0;
    let selectedId: number | undefined = undefined;
    let channelCostByChanel: ChannelCostByChanel | undefined = undefined;
    const locations: { name: string }[] = [];

    for (const channel of usedChannels) {
      channelCostByChanel = channelCostByChannels.get(channel);
      locations.push({ name: formatChannelName(channel) });
      if (oneTimeOverrideChannelCost) {
        cost = oneTimeOverrideChannelCost;
      } else if (!cost && channelCostDefaultId) {
        const byIdCost = channelById.get(channelCostDefaultId)?.cost;
        if (byIdCost) {
          cost = byIdCost;
          selectedId = channelCostDefaultId;
        }
      } else if (!cost) {
        const byVessel = channelByVesselId.get(channel, vesselId);
        if (byVessel) {
          cost = byVessel.cost;
          selectedId = byVessel.id;
          continue;
        }

        // Preference vessel class (primary vessel attributes) over vessel types
        if (vesselClass) {
          const byClass = channelByType.get(channel, vesselClass.name);
          if (byClass) {
            cost = byClass.cost;
            selectedId = byClass.id;
            continue;
          }
        }

        for (const type of vesselTypes) {
          const byType = channelByType.get(channel, type.name);
          if (byType) {
            cost = byType.cost;
            selectedId = byType.id;
          }
          if (cost) break; // As soon as finding a match exit the loop
        }

        // If there is no match for vessel and type
        if (cost) continue;
        const byName = channelByChannelName.get(channel);
        if (byName) {
          cost = byName?.cost;
          selectedId = byName?.id;
        }
      }
    }
    costs.set(pc.id, [
      {
        type: 'CHANNEL',
        name: 'Channel costs',
        value: cost,
        locations,
        numShipments: 1,
        options: {
          portCallEntry,
          oneTimeOverrideChannelCost,
          selectedId,
          channelCostByChanel,
        },
      },
    ]);
  }

  return costs;
}
